import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {apiFetch} from '../api';
import Checklist from './Checklist';
import NewChecklistModal from './NewChecklistModal';
import GroupSidebar from './GroupSidebar';

export default function ChecklistContainer() {
    const [checklistData, setChecklistData] = useState([]);
    const [groups, setGroups] = useState([]);
    const [selectedGroupId, setSelectedGroupId] = useState('all');
    const [showModal, setShowModal] = useState(false);
    const [focusedId, setFocusedId] = useState(null);

    useEffect(() => {
        if (!focusedId) return;
        const handleKeyDown = (e) => { if (e.key === 'Escape') setFocusedId(null); };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [focusedId]);

    useEffect(() => {
        document.body.classList.toggle('focus-mode', focusedId !== null);
        return () => document.body.classList.remove('focus-mode');
    }, [focusedId]);

    const focusChecklist = useCallback((id) => setFocusedId(id), []);
    const unfocusChecklist = useCallback(() => setFocusedId(null), []);

    // Ref keeps callbacks stable without stale closure issues
    const dataRef = useRef(checklistData);
    useEffect(() => { dataRef.current = checklistData; }, [checklistData]);

    useEffect(() => {
        const controller = new AbortController();
        apiFetch('/checklists', {signal: controller.signal})
            .then(res => res.json())
            .then(data => setChecklistData(data.sort((a, b) => a.id - b.id)))
            .catch(err => { if (err.name !== 'AbortError') throw err; });
        apiFetch('/checklist-groups', {signal: controller.signal})
            .then(res => res.json())
            .then(data => setGroups(data))
            .catch(err => { if (err.name !== 'AbortError') throw err; });
        return () => controller.abort();
    }, []);

    const visibleChecklists = useMemo(() => {
        if (selectedGroupId === 'all') return checklistData;
        if (selectedGroupId === 'starred') {
            return checklistData
                .filter(c => c.items.some(i => i.starred))
                .map(c => ({...c, items: c.items.filter(i => i.starred)}));
        }
        if (selectedGroupId === null) return checklistData.filter(c => c.groupId == null);
        return checklistData.filter(c => c.groupId === selectedGroupId);
    }, [checklistData, selectedGroupId]);

    const putChecklist = useCallback(async (checklistId, updated) => {
        const res = await apiFetch(`/checklists/${checklistId}`, {
            method: 'PUT',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify(updated),
        });
        const saved = await res.json();
        setChecklistData(prev => prev.map(c => c.id === checklistId ? saved : c));
    }, []);

    const deleteChecklist = useCallback(async (id) => {
        await apiFetch(`/checklists/${id}`, {method: 'DELETE'});
        setChecklistData(prev => prev.filter(c => c.id !== id));
        setFocusedId(prev => prev === id ? null : prev);
    }, []);

    const updateTitle = useCallback((checklistId, title) => {
        const checklist = dataRef.current.find(c => c.id === checklistId);
        putChecklist(checklistId, {...checklist, title});
    }, [putChecklist]);

    const updateItem = useCallback((checklistId, itemId, title) => {
        const checklist = dataRef.current.find(c => c.id === checklistId);
        const items = checklist.items.map(i => i.id === itemId ? {...i, title} : i);
        putChecklist(checklistId, {...checklist, items});
    }, [putChecklist]);

    const deleteItem = useCallback((checklistId, itemId) => {
        const checklist = dataRef.current.find(c => c.id === checklistId);
        const items = checklist.items.filter(i => i.id !== itemId);
        putChecklist(checklistId, {...checklist, items});
    }, [putChecklist]);

    const addItem = useCallback((checklistId, title) => {
        const checklist = dataRef.current.find(c => c.id === checklistId);
        const newItem = {id: Math.max(0, ...checklist.items.map(i => i.id)) + 1, title, checked: false, starred: false};
        putChecklist(checklistId, {...checklist, items: [newItem, ...checklist.items]});
    }, [putChecklist]);

    const starItem = useCallback((checklistId, itemId) => {
        const checklist = dataRef.current.find(c => c.id === checklistId);
        const items = checklist.items.map(i => i.id === itemId ? {...i, starred: !i.starred} : i);
        putChecklist(checklistId, {...checklist, items});
    }, [putChecklist]);

    const reorderItems = useCallback((checklistId, newItems) => {
        const checklist = dataRef.current.find(c => c.id === checklistId);
        const updated = {...checklist, items: newItems, sortMode: 'custom'};
        setChecklistData(prev => prev.map(c => c.id === checklistId ? updated : c));
        apiFetch(`/checklists/${checklistId}`, {
            method: 'PUT',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify(updated),
        }).then(res => res.json()).then(saved => {
            setChecklistData(prev => prev.map(c => c.id === checklistId ? saved : c));
        });
    }, []);

    const toggleSortMode = useCallback((checklistId) => {
        const checklist = dataRef.current.find(c => c.id === checklistId);
        if (checklist.sortMode === 'custom') {
            putChecklist(checklistId, {...checklist, sortMode: 'created_at'});
        } else {
            const sorted = [...checklist.items].sort((a, b) => a.id - b.id);
            putChecklist(checklistId, {...checklist, items: sorted, sortMode: 'custom'});
        }
    }, [putChecklist]);

    const toggleItem = useCallback((checklistId, itemId) => {
        const checklist = dataRef.current.find(c => c.id === checklistId);
        const items = checklist.items.map(i => i.id === itemId ? {...i, checked: !i.checked} : i);
        putChecklist(checklistId, {...checklist, items});
    }, [putChecklist]);

    const addNewChecklist = useCallback(async (title) => {
        const body = {title, items: []};
        if (selectedGroupId !== 'all') {
            body.groupId = selectedGroupId; // null (Default) or a group id number
        }
        const res = await apiFetch('/checklists', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify(body),
        });
        const created = await res.json();
        setChecklistData(prev => [...prev, created]);
        setFocusedId(created.id);
    }, [selectedGroupId]);

    const handleModalConfirm = useCallback((title) => {
        addNewChecklist(title);
        setShowModal(false);
    }, [addNewChecklist]);

    const renameGroup = useCallback(async (id, name) => {
        await apiFetch(`/checklist-groups/${id}`, {
            method: 'PUT',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({name}),
        });
        setGroups(prev => prev.map(g => g.id === id ? {...g, name} : g));
    }, []);

    const createGroup = useCallback(async (name) => {
        const res = await apiFetch('/checklist-groups', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({name}),
        });
        const group = await res.json();
        setGroups(prev => [...prev, group]);
    }, []);

    const deleteGroup = useCallback(async (id) => {
        const res = await apiFetch(`/checklist-groups/${id}`, {method: 'DELETE'});
        if (!res.ok) return;
        setGroups(prev => prev.filter(g => g.id !== id));
        setSelectedGroupId(prev => prev === id ? 'all' : prev);
    }, []);

    return (
        <div className="checklist-layout">
            <GroupSidebar
                groups={groups}
                selectedGroupId={selectedGroupId}
                checklistData={checklistData}
                onSelect={setSelectedGroupId}
                onAddGroup={createGroup}
                onRenameGroup={renameGroup}
                onDeleteGroup={deleteGroup}
            />
            <div className="checklist-container-root">
                <div>
                    <button className="add-checklist-button" onClick={() => setShowModal(true)}>
                        + New List
                    </button>
                </div>
                {showModal ? (
                    <NewChecklistModal onConfirm={handleModalConfirm} onCancel={() => setShowModal(false)}/>
                ) : null}
                {focusedId !== null && (
                    <div className="focus-backdrop" onClick={() => setFocusedId(null)} />
                )}
                <div className={`checklists-container${focusedId !== null ? ' checklists-container--hidden' : ''}`}>
                    {visibleChecklists.map(checklist => (
                        <Checklist
                            key={checklist.id}
                            data={checklist}
                            onDelete={deleteChecklist}
                            onAddItem={addItem}
                            onToggleItem={toggleItem}
                            onDeleteItem={deleteItem}
                            onUpdateTitle={updateTitle}
                            onUpdateItem={updateItem}
                            onReorderItems={reorderItems}
                            onToggleSortMode={toggleSortMode}
                            onStarItem={starItem}
                            readOnly={selectedGroupId === 'starred'}
                            isFocused={focusedId === checklist.id}
                            onFocus={focusChecklist}
                            onUnfocus={unfocusChecklist}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}
